import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface User {
  id: string
  email: string
  name: string
  role: string
  centre_id?: string
  centre_name?: string
  centre_code?: string
  is_impersonating?: boolean
  original_user_id?: string
  original_role?: string
}

interface AuthState {
  isAuthenticated: boolean
  user: User | null
  token: string | null
  loading: boolean
  error: string | null
}

// Load initial state from localStorage
const loadAuthState = (): AuthState => {
  try {
    const token = localStorage.getItem('token')
    const userStr = localStorage.getItem('user')
    
    if (token && userStr) {
      // Basic token validation - check if it's a JWT and not expired
      try {
        const tokenParts = token.split('.')
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]))
          const currentTime = Date.now() / 1000
          
          // Check if token is expired
          if (payload.exp && payload.exp < currentTime) {
            console.log('Token expired, clearing auth state')
            localStorage.removeItem('token')
            localStorage.removeItem('user')
            return {
              isAuthenticated: false,
              user: null,
              token: null,
              loading: false,
              error: null
            }
          }
        }
      } catch (tokenError) {
        console.error('Invalid token format, clearing auth state:', tokenError)
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        return {
          isAuthenticated: false,
          user: null,
          token: null,
          loading: false,
          error: null
        }
      }
      
      const user = JSON.parse(userStr)
      return {
        isAuthenticated: true,
        user,
        token,
        loading: false,
        error: null
      }
    }
  } catch (error) {
    console.error('Error loading auth state from localStorage:', error)
    // Clear corrupted data
    localStorage.removeItem('token')
    localStorage.removeItem('user')
  }
  
  return {
    isAuthenticated: false,
    user: null,
    token: null,
    loading: false,
    error: null
  }
}

const initialState: AuthState = loadAuthState()

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.isAuthenticated = true
      state.user = action.payload.user
      state.token = action.payload.token
      state.error = null
      
      // Save to localStorage
      localStorage.setItem('token', action.payload.token)
      localStorage.setItem('user', JSON.stringify(action.payload.user))
    },
    logout: (state) => {
      state.isAuthenticated = false
      state.user = null
      state.token = null
      state.error = null
      
      // Clear localStorage
      localStorage.removeItem('token')
      localStorage.removeItem('user')
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },
    clearError: (state) => {
      state.error = null
    }
  }
})

export const { login, logout, setLoading, setError, clearError } = authSlice.actions
export default authSlice.reducer
